'use client';

/**
 * Application Entry Point
 *
 * This is the main entry point for the RWA Terminal Internal Console.
 * It sets up the React application with:
 * - StrictMode for development checks
 * - AppProviders for global context (Query, Auth, Toast)
 * - RouterProvider for React Router v7 data-aware routing
 *
 * The application architecture:
 * - main.tsx: Entry point with providers
 * - router.tsx: Route definitions with lazy loading and auth guards
 * - providers.tsx: Global providers (QueryClient, Auth, Toast)
 * - Layout.tsx: Common layout with Sidebar and Header
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import './i18n';
import { AppProviders } from '@/app/providers';
import { router } from '@/app/router';
import { initWebVitals } from '@/lib/webVitals';
import './index.css';

/**
 * Get the root element from the DOM
 * Throws an error if the root element is not found
 */
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error(
    'Failed to find the root element. Make sure your index.html has a <div id="root"></div>'
  );
}

/**
 * Create the React root and render the application
 *
 * Composition:
 * 1. StrictMode - Enables additional development checks
 * 2. AppProviders - Sets up QueryClient, Auth initialization, and Toaster
 * 3. RouterProvider - Handles routing with the configured router
 */
createRoot(rootElement).render(
  <StrictMode>
    <AppProviders>
      <RouterProvider router={router} />
    </AppProviders>
  </StrictMode>
);

// Initialize Web Vitals monitoring after app mounts
initWebVitals();
